import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Per-player price breakdown in the response.
 */
export interface PlayerPriceBreakdown {
  userId: string | null;
  userName: string;
  isOrganizer: boolean;
  /** True if this is a guest slot (no registered user, priced at default rate) */
  isGuestSlot: boolean;
  /** Player's share of the price in EUR (with membership discount) */
  priceShare: number;
  /** Player's share of the base price in EUR (without discount) */
  basePriceShare: number;
  /** Savings compared to default rate in EUR (basePriceShare - priceShare) */
  savings: number;
  /** Name of the membership that was applied for pricing */
  membershipAppliedName?: string;
  /** Pricing rule IDs used for this player's price calculation */
  pricingRuleIds: string[];
  /**
   * True when this participant consumes one guest unit of the organizer's
   * guest allowance (member-only window).
   */
  countsAsGuestUnit?: boolean;
  /** Flat pro-rated guest fee in EUR for this guest unit (explicit line item) */
  guestFee?: number;
}

/**
 * Response from multi-player price calculation endpoint.
 */
export interface MultiPlayerPriceResponse {
  /** Total price in EUR (sum of all player shares) */
  totalPrice: number;
  /** Total base price in EUR (before discounts) */
  baseTotalPrice: number;
  /** Number of players */
  playerCount: number;
  /** Number of registered players (non-guests) */
  registeredPlayerCount: number;
  /** Number of guest slots (priced at default rate) */
  guestSlotCount: number;
  /** Duration in minutes */
  durationMinutes: number;
  /** Per-player price breakdown */
  playerBreakdown: PlayerPriceBreakdown[];
  /** Total savings compared to if all players paid default rate */
  totalSavings: number;
  /** All pricing rule IDs used across the booking duration */
  pricingRuleIds: string[];
  /** Sum of all guest-unit fees in EUR (0 when none applied) */
  guestFeeTotal?: number;
  /** Guest units this booking consumes from the organizer's allowance */
  guestUnitsRequired?: number;
  /** Remaining units of the selected allowance config BEFORE this booking */
  remainingGuestUnits?: number;
  /** Guest fee rate of the selected config in EUR per hour */
  guestFeePerHour?: number;
  /** The guest allowance config all of this booking's guest units consume */
  guestAllowanceConfigId?: string;
  /**
   * Stripe sub-minimum bump in EUR added at quote time ("Mindestbetrag");
   * already included in totalPrice.
   */
  minimumChargeAdjustment?: number;
}

/**
 * Request payload for multi-player price calculation.
 */
export interface CalculateMultiPlayerPriceRequest {
  organizationId: string;
  categoryId: string;
  /** All player user IDs (including the organizer/current user) */
  playerUserIds: string[];
  /** Number of guest slots (priced at default rate). Only valid when pricingPerPlayer is enabled. */
  guestSlotCount?: number;
  /** Total booking duration in minutes */
  durationMinutes: number;
  /** Booking date in YYYY-MM-DD format */
  bookingDate: string;
  /** Booking start time in HH:mm format */
  bookingTime: string;
  /**
   * Enable per-player pricing mode.
   * When true, each player's individual membership determines their share.
   * When false (default), organizer's rate is used and divided equally among all players.
   */
  pricingPerPlayer?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PricingService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Calculate price breakdown for multiple players.
   * Each player's share is calculated based on their individual membership.
   *
   * @param request The calculation request with player IDs, duration, date/time
   * @returns Observable with the price breakdown for all players
   */
  calculateMultiPlayerPrice(request: CalculateMultiPlayerPriceRequest): Observable<MultiPlayerPriceResponse> {
    return this.http.post<MultiPlayerPriceResponse>(
      `${this.apiUrl}/pricing-rules/calculate-multi-player`,
      request
    );
  }
}
